import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Body,
  Box,
  Button,
  Caption,
  EmptyState,
  Eyebrow,
  I,
  Icon,
  Label,
  Pill,
  Row,
  Stack,
  Toggle,
  useDelayedLoading,
} from '@leanin/ui';
import {
  Sortable,
  type SortableItem,
  type SortableLabels,
} from '@leanin/ui/client';
import { Eye } from 'lucide-react';
import { useTranslations } from 'use-intl';
import {
  INTAKE_CONSENT_STATEMENT_KEYS,
  MAX_CHOICE_OPTION_CHARS,
  MAX_CHOICE_OPTIONS,
  MAX_PROMPT_CHARS,
  MAX_QUESTIONS,
  MIN_CHOICE_OPTIONS,
  type IntakeFormKind,
  type IntakeQuestionType,
} from '@leanin/shared/intake';
import { useIntakeForm } from '@/lib/queries/groups';
import { useUpsertIntakeForm } from '@/lib/mutations/groups';
import { failureFromError, isNotFoundApiError } from '@/lib/api-error';
import { AdminFormPageSkeleton } from '../AdminFormPageSkeleton';
import {
  CUSTOM_QUESTION_TYPES,
  type BuilderDraft,
  type DraftQuestion,
  type FormErrorCode,
  type QuestionErrorCode,
  type RecommendedTemplate,
  addCustomQuestion,
  availableRecommendedTemplates,
  canAddQuestion,
  changeQuestionType,
  configToDraft,
  draftToUpsertBody,
  freeformQuestions,
  isDirty,
  reorderFreeform,
  reorderRecommended,
  selectedRecommendedRows,
  toggleRecommended,
  validateDraft,
} from './intake-builder-model';
import { QuestionRow } from './QuestionRow';
import { IntakeConsentNotice } from './IntakeConsentNotice';
import { RequestToJoinGroupModal } from '@/components/groups/RequestToJoinGroupModal';
import { type ApplicantIntakeForm } from '@/components/intake';
import styles from './join-questions.module.css';

type Props = {
  readonly groupId: string;
  readonly formKind: IntakeFormKind;
  /** Group display name — feeds the preview's "Request to join {name}" header. */
  readonly groupName: string;
  /** Reports the builder's unsaved-changes state up so the section can guard a
   *  form switch. Must be stable (memoized by the caller). */
  readonly onDirtyChange?: (dirty: boolean) => void;
};

type SortableQuestion = SortableItem & { readonly raw: DraftQuestion };

// Render an unsaved draft as the applicant would see it: the preview reuses the
// real applicant modal, so it needs the wire form shape. Blank prompts fall
// back to the placeholder and blank choice options are dropped — exactly how a
// saved form resolves them.
function draftToApplicantForm(
  questions: readonly DraftQuestion[],
  promptPlaceholder: string,
): ApplicantIntakeForm {
  return {
    formVersionId: 'preview',
    versionNumber: 0,
    questions: questions.map((q) => ({
      id: q.id,
      type: q.type,
      prompt: q.prompt.trim().length > 0 ? q.prompt : promptPlaceholder,
      required: q.required,
      options: q.options.map((o) => o.trim()).filter((o) => o.length > 0),
      ...(q.prefillSource ? { prefillSource: q.prefillSource } : {}),
    })),
    consentStatementKeys: [...INTAKE_CONSENT_STATEMENT_KEYS],
  };
}

export function IntakeFormBuilder({
  groupId,
  formKind,
  groupName,
  onDirtyChange,
}: Props) {
  const t = useTranslations('Group');
  const configQuery = useIntakeForm(groupId, formKind);
  const upsert = useUpsertIntakeForm();
  const config = configQuery.data;

  const [draft, setDraft] = useState<BuilderDraft | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (config !== undefined && draft === null) setDraft(configToDraft(config));
  }, [config, draft]);

  const dirty =
    draft !== null && config !== undefined ? isDirty(draft, config) : false;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (dirty) setJustSaved(false);
  }, [dirty]);

  useEffect(() => {
    if (!justSaved) return;
    const id = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(id);
  }, [justSaved]);

  const validation = useMemo(
    () => (draft !== null ? validateDraft(draft) : null),
    [draft],
  );

  const recommendedPrompt = (key: string): string => {
    switch (key) {
      case 'why_join':
        return t('admin.joinQuestions.recommended.why_join');
      case 'region':
        return t('admin.joinQuestions.recommended.region');
      case 'sector':
        return t('admin.joinQuestions.recommended.sector');
      case 'code_of_conduct':
        return t('admin.joinQuestions.recommended.code_of_conduct');
      default:
        return '';
    }
  };

  const errorCopy = (code: QuestionErrorCode): string => {
    switch (code) {
      case 'prompt_required':
        return t('admin.joinQuestions.errors.prompt_required');
      case 'prompt_too_long':
        return t('admin.joinQuestions.errors.prompt_too_long', { max: MAX_PROMPT_CHARS });
      case 'options_min':
        return t('admin.joinQuestions.errors.options_min', { min: MIN_CHOICE_OPTIONS });
      case 'options_max':
        return t('admin.joinQuestions.errors.options_max', { max: MAX_CHOICE_OPTIONS });
      case 'options_duplicate':
        return t('admin.joinQuestions.errors.options_duplicate');
      case 'option_too_long':
        return t('admin.joinQuestions.errors.option_too_long', { max: MAX_CHOICE_OPTION_CHARS });
      case 'option_required':
        return t('admin.joinQuestions.errors.option_required');
    }
  };

  const formErrorCopy = (code: FormErrorCode): string => {
    switch (code) {
      case 'too_many_questions':
        return t('admin.joinQuestions.errors.too_many_questions', { max: MAX_QUESTIONS });
      case 'no_questions_when_enabled':
        return t('admin.joinQuestions.errors.fixBeforeSaving');
    }
  };

  const freeform = useMemo(
    () => (draft !== null ? freeformQuestions(draft) : []),
    [draft],
  );
  const recommendedSelected = useMemo(
    () => (draft !== null ? selectedRecommendedRows(draft) : []),
    [draft],
  );
  const recommendedAvailable = useMemo(
    () => (draft !== null ? availableRecommendedTemplates(draft) : []),
    [draft],
  );

  const labelFor = (q: DraftQuestion): string =>
    q.prompt.trim().length > 0
      ? q.prompt
      : t('admin.joinQuestions.question.promptPlaceholder');

  const freeformItems = useMemo<SortableQuestion[]>(
    () => freeform.map((q) => ({ id: q.id, label: labelFor(q), raw: q })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- labelFor only reads `t`
    [freeform, t],
  );
  const recommendedItems = useMemo<SortableQuestion[]>(
    () =>
      recommendedSelected.map((r) => ({
        id: r.question.id,
        label: labelFor(r.question),
        raw: r.question,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- labelFor only reads `t`
    [recommendedSelected, t],
  );

  const srStrings = useMemo(
    () => ({
      onPickUp: (item: string) => t('admin.joinQuestions.reorderSr.pickUp', { item }),
      onMove: (item: string, position: number, total: number) =>
        t('admin.joinQuestions.reorderSr.move', { item, position, total }),
      onDrop: (item: string, position: number) =>
        t('admin.joinQuestions.reorderSr.drop', { item, position }),
      onCancel: (item: string) => t('admin.joinQuestions.reorderSr.cancel', { item }),
    }),
    [t],
  );

  const sortableLabels = useMemo<Partial<SortableLabels>>(
    () => ({
      dragHandle: (item) => t('admin.joinQuestions.reorderControls.dragHandle', { item }),
      reorderButton: (item) => t('admin.joinQuestions.reorderControls.reorderButton', { item }),
      list: t('admin.joinQuestions.reorderControls.list'),
      picker: (item, position, total) =>
        t('admin.joinQuestions.reorderControls.picker', { item, position, total }),
      moveUp: (item) => t('admin.joinQuestions.reorderControls.moveUp', { item }),
      moveDown: (item) => t('admin.joinQuestions.reorderControls.moveDown', { item }),
      closePicker: t('admin.joinQuestions.reorderControls.closePicker'),
      reorderText: t('admin.joinQuestions.reorderControls.reorderText'),
      doneText: t('admin.joinQuestions.reorderControls.doneText'),
    }),
    [t],
  );

  const showSkeleton = useDelayedLoading(
    !configQuery.isError && (config === undefined || draft === null),
  );

  if (configQuery.isError) {
    return isNotFoundApiError(configQuery.error) ? (
      <EmptyState
        icon={I.lock}
        title={t('admin.joinQuestions.unavailable.title')}
        body={t('admin.joinQuestions.unavailable.body')}
        bordered
      />
    ) : (
      <EmptyState
        icon={I.alert}
        title={t('admin.joinQuestions.loadError.title')}
        body={t('admin.joinQuestions.loadError.body')}
        bordered
        primary={
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              void configQuery.refetch();
            }}
          >
            {t('admin.joinQuestions.loadError.retry')}
          </Button>
        }
      />
    );
  }

  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={6} withAction />;
  }
  if (config === undefined || draft === null) return null;

  const typeLabel = (type: IntakeQuestionType): string =>
    t(`admin.joinQuestions.type.${type}`);

  const customTypeOptions = CUSTOM_QUESTION_TYPES.map((type) => ({
    value: type,
    label: typeLabel(type),
  }));

  const setEnabled = (enabled: boolean) => setDraft({ ...draft, enabled });

  const patchQuestion = (id: string, patch: Partial<DraftQuestion>) =>
    setDraft({
      ...draft,
      questions: draft.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    });

  const setQuestionType = (id: string, type: IntakeQuestionType) =>
    setDraft({
      ...draft,
      questions: draft.questions.map((q) =>
        q.id === id ? changeQuestionType(q, type) : q,
      ),
    });

  const removeQuestion = (id: string) => {
    if (editingId === id) setEditingId(null);
    setDraft({ ...draft, questions: draft.questions.filter((q) => q.id !== id) });
  };

  const addCustom = () => {
    if (!canAddQuestion(draft)) return;
    const id = crypto.randomUUID();
    setDraft(addCustomQuestion(draft, id));
    setEditingId(id); // opens the new card straight into its editor (autofocus)
  };

  const toggleRecommendedRow = (template: RecommendedTemplate) => {
    const present = draft.questions.some((q) => q.templateKey === template.key);
    if (present && editingId !== null) {
      const q = draft.questions.find((x) => x.templateKey === template.key);
      if (q && editingId === q.id) setEditingId(null);
    }
    setDraft(toggleRecommended(draft, template, recommendedPrompt(template.key)));
  };

  const handleReorderFreeform = (items: readonly SortableQuestion[]) =>
    setDraft(reorderFreeform(draft, items.map((i) => i.raw)));
  const handleReorderRecommended = (items: readonly SortableQuestion[]) =>
    setDraft(reorderRecommended(draft, items.map((i) => i.raw)));

  const handleSave = () => {
    const result = validateDraft(draft);
    if (!result.valid) {
      setShowErrors(true);
      setSaveError(t('admin.joinQuestions.errors.fixBeforeSaving'));
      return;
    }
    setSaveError(null);
    upsert.mutate(
      { groupId, formKind, body: draftToUpsertBody(draft) },
      {
        onSuccess: (saved) => {
          setDraft(configToDraft(saved));
          setShowErrors(false);
          setEditingId(null);
          setJustSaved(true);
        },
        onError: (err) => {
          setSaveError(
            failureFromError(err, t('admin.joinQuestions.saveError')).message,
          );
        },
      },
    );
  };

  const handleDiscard = () => {
    setDraft(configToDraft(config));
    setShowErrors(false);
    setSaveError(null);
    setEditingId(null);
  };

  const canAdd = canAddQuestion(draft);
  const questionErrorFor = (id: string): string | null => {
    if (!showErrors || validation === null) return null;
    const code = validation.questionErrors[id];
    return code === undefined ? null : errorCopy(code);
  };
  const hasError = (id: string): boolean =>
    showErrors && validation !== null && validation.questionErrors[id] !== undefined;
  const isExpanded = (id: string): boolean => editingId === id || hasError(id);
  const toggleEdit = (id: string) =>
    setEditingId((current) => (current === id ? null : id));

  const selectedHelper = (template: RecommendedTemplate): ReactNode => {
    if (template.prefillSource !== undefined) {
      return (
        <Row gap="xs" align="center">
          <Icon size="sm" color="var(--success)">
            {I.check}
          </Icon>
          <Body size="sm" color="success">
            {t('admin.joinQuestions.recommended.prefillNote')}
          </Body>
        </Row>
      );
    }
    if (template.key === 'why_join') {
      return <Caption>{t('admin.joinQuestions.recommended.whyJoinNote')}</Caption>;
    }
    return null;
  };

  return (
    <Stack gap="lg">
      <Box border="hairline" rounded="lg" bg="card" style={{ overflow: 'hidden' }}>
        <Row justify="space-between" align="center" gap="md" px="xl" py="lg">
          <Row gap="sm" align="center" style={{ minWidth: 0 }}>
            <Eyebrow>{t('admin.joinQuestions.title')}</Eyebrow>
            <Pill size="xs" tone="emerald">
              {t('admin.joinQuestions.newBadge')}
            </Pill>
          </Row>
          <Row gap="sm" align="center" style={{ flexShrink: 0 }}>
            <Label>
              {draft.enabled
                ? t('admin.joinQuestions.enable.on')
                : t('admin.joinQuestions.enable.off')}
            </Label>
            <Toggle
              on={draft.enabled}
              onChange={() => setEnabled(!draft.enabled)}
              label={t('admin.joinQuestions.enable.srLabel')}
            />
          </Row>
        </Row>

        <Stack gap="xl" px="xl" pb="xl">
          <Stack gap="xs">
            <Body color="mute">
              {t('admin.joinQuestions.panelIntro', { formKind })}
            </Body>
            {!draft.enabled && (
              <Caption>{t('admin.joinQuestions.enable.offNote')}</Caption>
            )}
          </Stack>

          {/* RECOMMENDED */}
          <Stack gap="none">
            <Row gap="xs" align="center" pb="sm">
              <Eyebrow>{t('admin.joinQuestions.sections.recommended')}</Eyebrow>
              <Icon size="sm" color="var(--muted-foreground)">
                {I.spark}
              </Icon>
            </Row>

            {recommendedSelected.length > 0 && (
              <Sortable<SortableQuestion>
                items={recommendedItems}
                onReorder={handleReorderRecommended}
                srStrings={srStrings}
                labels={sortableLabels}
                className={styles.recommendedList}
              >
                {(item, dragHandle) => {
                  const idx = recommendedSelected.findIndex(
                    (r) => r.question.id === item.raw.id,
                  );
                  const row = recommendedSelected[idx];
                  if (row === undefined) return null;
                  const { template, question } = row;
                  return (
                    <QuestionRow
                      dragHandle={dragHandle}
                      divider={idx > 0}
                      padX="none"
                      padY="md"
                      checked
                      onToggleChecked={() => toggleRecommendedRow(template)}
                      checkboxLabel={t('admin.joinQuestions.recommended.checkboxLabel', {
                        prompt: question.prompt,
                      })}
                      prompt={question.prompt}
                      helper={selectedHelper(template)}
                      typeLabel={typeLabel(question.type)}
                      question={question}
                      editing={isExpanded(question.id)}
                      onEdit={() => toggleEdit(question.id)}
                      editLabel={t('admin.joinQuestions.question.editLabel', {
                        prompt: question.prompt,
                      })}
                      collapseLabel={t('admin.joinQuestions.question.collapseLabel', {
                        prompt: question.prompt,
                      })}
                      promptPlaceholder={t('admin.joinQuestions.question.promptPlaceholder')}
                      allowTypeChange={false}
                      onPatch={(patch) => patchQuestion(question.id, patch)}
                      errorMessage={questionErrorFor(question.id)}
                    />
                  );
                }}
              </Sortable>
            )}

            {recommendedAvailable.map((template, availIdx) => {
              const prompt = recommendedPrompt(template.key);
              return (
                <QuestionRow
                  key={template.key}
                  handlePlaceholder
                  divider={recommendedSelected.length > 0 || availIdx > 0}
                  padX="none"
                  padY="md"
                  muted
                  checked={false}
                  checkboxDisabled={!canAdd}
                  onToggleChecked={() => toggleRecommendedRow(template)}
                  checkboxLabel={t('admin.joinQuestions.recommended.checkboxLabel', { prompt })}
                  prompt={prompt}
                  helper={<Caption>{t('admin.joinQuestions.recommended.tapToAdd')}</Caption>}
                  typeLabel={typeLabel(template.type)}
                />
              );
            })}
          </Stack>

          {/* FREEFORM */}
          <Stack gap="sm">
            <Eyebrow>{t('admin.joinQuestions.sections.freeform')}</Eyebrow>
            {freeform.length > 0 && (
              <Sortable<SortableQuestion>
                items={freeformItems}
                onReorder={handleReorderFreeform}
                srStrings={srStrings}
                labels={sortableLabels}
                className={styles.freeformList}
              >
                {(item, dragHandle) => {
                  const q = item.raw;
                  const prompt =
                    q.prompt.trim().length > 0
                      ? q.prompt
                      : t('admin.joinQuestions.question.promptPlaceholder');
                  return (
                    <QuestionRow
                      tinted
                      rounded
                      accentPill
                      padX="md"
                      padY="sm"
                      dragHandle={dragHandle}
                      checked
                      onToggleChecked={(checked) => {
                        if (!checked) removeQuestion(q.id);
                      }}
                      checkboxLabel={t('admin.joinQuestions.question.includeLabel', { prompt })}
                      prompt={prompt}
                      typeLabel={typeLabel(q.type)}
                      question={q}
                      editing={isExpanded(q.id)}
                      onEdit={() => toggleEdit(q.id)}
                      editLabel={t('admin.joinQuestions.question.editLabel', { prompt })}
                      collapseLabel={t('admin.joinQuestions.question.collapseLabel', { prompt })}
                      promptPlaceholder={t('admin.joinQuestions.question.promptPlaceholder')}
                      typeSelectLabel={t('admin.joinQuestions.question.typeLabel')}
                      allowTypeChange
                      typeOptions={customTypeOptions}
                      onPatch={(patch) => patchQuestion(q.id, patch)}
                      onChangeType={(type) => setQuestionType(q.id, type)}
                      errorMessage={questionErrorFor(q.id)}
                      onRemove={() => removeQuestion(q.id)}
                      removeLabel={t('admin.joinQuestions.question.remove')}
                    />
                  );
                }}
              </Sortable>
            )}
            <Row gap="md" align="center">
              <Button
                variant="link"
                size="md"
                icon={I.plus}
                onClick={addCustom}
                disabled={!canAdd}
              >
                {t('admin.joinQuestions.addQuestion')}
              </Button>
              {!canAdd && (
                <Caption>
                  {t('admin.joinQuestions.limitReached', { max: MAX_QUESTIONS })}
                </Caption>
              )}
            </Row>
          </Stack>

          <IntakeConsentNotice />
        </Stack>

        <Box px="xl" py="md" style={{ borderTop: '1px solid var(--border)' }}>
          <Row justify="space-between" align="center" gap="md" wrap>
            <div aria-live="polite" style={{ minWidth: 0 }}>
              {saveError !== null ? (
                <Body size="sm" color="destructive">
                  {saveError}
                </Body>
              ) : justSaved ? (
                <Body size="sm" color="success">
                  {t('admin.joinQuestions.footer.saved')}
                </Body>
              ) : (
                <Body size="sm" color="mute">
                  {t('admin.joinQuestions.footer.count', {
                    count: draft.questions.length,
                    max: MAX_QUESTIONS,
                  })}
                </Body>
              )}
              {showErrors && validation?.formError != null && saveError === null && (
                <Body size="sm" color="destructive">
                  {formErrorCopy(validation.formError)}
                </Body>
              )}
            </div>
            <Row gap="sm" align="center">
              {dirty && (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleDiscard}
                  disabled={upsert.isPending}
                >
                  {t('admin.joinQuestions.footer.discard')}
                </Button>
              )}
              <Button
                variant="secondary"
                size="md"
                icon={<Eye size={16} strokeWidth={1.75} aria-hidden />}
                onClick={() => setPreviewOpen(true)}
              >
                {t('admin.joinQuestions.footer.previewForm')}
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={I.check}
                onClick={handleSave}
                disabled={!dirty}
                loading={upsert.isPending}
              >
                {t('admin.joinQuestions.footer.save')}
              </Button>
            </Row>
          </Row>
        </Box>
      </Box>

      {/* Preview reuses the real applicant modal in preview mode — same shell,
          header, fields, separators, consent, and footer as the live
          Request-to-join form. Mounted only while open so its data hooks stay
          idle in the background; it renders the current draft and never submits. */}
      {previewOpen && (
        <RequestToJoinGroupModal
          open
          onClose={() => setPreviewOpen(false)}
          isPreview
          previewForm={draftToApplicantForm(
            draft?.questions ?? [],
            t('admin.joinQuestions.question.promptPlaceholder'),
          )}
          groupId={groupId}
          groupKind={formKind === 'member_to_circle' ? 'circle' : 'network'}
          groupName={groupName}
          leader={null}
          onSubmit={async () => ({ ok: true })}
        />
      )}
    </Stack>
  );
}
